/**
 * API service integrations for the Salzburg Cab authentication modal.
 * Implements real network requests to the backend server with standard headers and error handling.
 */

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  warning?: string;
}

export interface AuthUser {
  id?: number;
  email: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  phone?: string;
  [key: string]: any;
}

export interface AuthPayload {
  token?: string;
  user?: AuthUser;
}

export function getAuthToken(response: any): string | undefined {
  return (
    response?.data?.access ||
    response?.data?.access_token ||
    response?.data?.token ||
    response?.data?.tokens?.access ||
    response?.access ||
    response?.access_token ||
    response?.token ||
    response?.tokens?.access
  )
}

export function getAuthUser(response: any): AuthUser | undefined {
  return response?.data?.user || response?.user
}

export function getRefreshToken(response: any): string | undefined {
  return (
    response?.data?.refresh ||
    response?.data?.refresh_token ||
    response?.refresh ||
    response?.refresh_token
  )
}

export function saveAuthSession(response: any, fallbackEmail: string): void {
  const token = getAuthToken(response)
  const refresh = getRefreshToken(response)
  const user = getAuthUser(response)
  
  
  const cachedProfile = localStorage.getItem('salzburg_profile_details')
  const authenticatedEmail = user?.email || fallbackEmail
  if (cachedProfile) {
    try {
      const parsed = JSON.parse(cachedProfile) as { email?: string }
      if (parsed.email && parsed.email.toLowerCase() !== authenticatedEmail.toLowerCase()) {
        localStorage.removeItem('salzburg_profile_details')
      }
    } catch {
      localStorage.removeItem('salzburg_profile_details')
    }
  }
  
  if (token) {
    localStorage.setItem('auth_token', token)
  }

  if (refresh) {
    localStorage.setItem('refresh_token', refresh)
  }

  if (user && typeof user === 'object') {
    localStorage.setItem('user_data', JSON.stringify(user))
    const authenticatedUserEmail = user.email || fallbackEmail
    if (authenticatedUserEmail) {
      localStorage.setItem('user_email', authenticatedUserEmail)
    }

    try {
      const existing = localStorage.getItem('salzburg_profile_details')
      const parsed = existing ? JSON.parse(existing) : {}
      const fullName = (user.name || user.full_name || [user.first_name, user.last_name].filter(Boolean).join(' ') || '').trim()
      const userPhone = (user.phone || user.phone_number || user.phoneNumber || user.mobile || '').trim()
      const userAvatar = user.avatar || user.picture || user.profile_image || user.profile_photo || ''
      localStorage.setItem(
        'salzburg_profile_details',
        JSON.stringify({
          ...parsed,
          name: fullName || parsed.name || (authenticatedUserEmail ? authenticatedUserEmail.split('@')[0] : ''),
          email: authenticatedUserEmail || parsed.email || '',
          phone: userPhone || parsed.phone || '',
          ...(userAvatar || parsed.avatar ? { avatar: userAvatar || parsed.avatar } : {}),
        }),
      )
    } catch {}
    return
  }

  localStorage.removeItem('user_data')
  if (fallbackEmail) {
    localStorage.setItem('user_email', fallbackEmail)
  }
}

/**
 * Remove all auth-related data from localStorage.
 */
export function clearAuthSession(): void {
  localStorage.removeItem('auth_token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('user_email')
  localStorage.removeItem('user_data')
}

/**
 * Backend API Configurations.
 * Developers can define VITE_API_BASE_URL and VITE_API_KEY in their .env file.
 * Defaults to 72.61.92.71 to assist local development.
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://72.61.92.71:8000';
export const API_KEY = import.meta.env.VITE_API_KEY || ''; // Optional third-party integration key

/**
 * Sign In API request (POST)
 */
export async function apiSignIn(email: string, password: string): Promise<ApiResponse<{ token: string; user: { email: string } }>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(API_KEY ? { 'X-API-Key': API_KEY } : {})
      },
      body: JSON.stringify({ email, password })
    });

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || 'Login failed.',
        warning: result?.warning || 'Please check your inputs and try again.'
      };
    }

    saveAuthSession(result, email);

    return {
      success: true,
      message: result?.message || 'Login successful!',
      data: result?.data || result
    };
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach backend API at ${API_BASE_URL}. Ensure your server is running and CORS is configured.`
    };
  }
}

export async function apiGoogleSignIn(token: string, tokenType: 'id_token' | 'access_token' = 'id_token'): Promise<ApiResponse<AuthPayload>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/login/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(API_KEY ? { 'X-API-Key': API_KEY } : {})
      },
      body: JSON.stringify(tokenType === 'access_token' ? { access_token: token } : { id_token: token })
    });

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || 'Google sign-in failed.',
        warning: result?.warning || 'Please try again with a valid Google account.'
      };
    }

    const authEmail = getAuthUser(result)?.email || '';
    saveAuthSession(result, authEmail);

    return {
      success: true,
      message: result?.message || 'Google sign-in successful!',
      data: result?.data || result
    };
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach backend API at ${API_BASE_URL}. Ensure your server is running and CORS is configured.`
    };
  }
}

/**
 * Sign Up / Create Account API request (POST)
 */
export async function apiSignUp(email: string, phone: string): Promise<ApiResponse<{ email: string }>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(API_KEY ? { 'X-API-Key': API_KEY } : {})
      },
      body: JSON.stringify({ email, phone })
    });

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || 'Registration failed.',
        warning: result?.warning || 'Please check your inputs.'
      };
    }

    return {
      success: true,
      message: result?.message || 'Account created successfully! An OTP has been sent.',
      data: result?.data || { email }
    };
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach registration endpoint at ${API_BASE_URL}.`
    };
  }
}

import { API_CONFIG } from '../config';

/**
 * OTP Verification API request (POST)
 */
export async function apiVerifyOtp(email: string, otpCode: string): Promise<ApiResponse> {
  // If explicitly configured for mock data and no real backend URL:
  if (!API_CONFIG.isRealData && !import.meta.env.VITE_API_BASE_URL) {
    await new Promise((r) => setTimeout(r, 600));
    // Simulate invalid OTP if user entered 000000
    if (otpCode === '000000') {
      return {
        success: false,
        message: 'Invalid verification code.',
        warning: 'The code you entered is incorrect. Please check and try again.'
      };
    }
    return {
      success: true,
      message: 'OTP verified successfully!'
    };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/verify-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(API_KEY ? { 'X-API-Key': API_KEY } : {})
      },
      body: JSON.stringify({ email, otpCode, otp: otpCode })
    }).catch(() => null);

    if (!response) {
      if (!API_CONFIG.isRealData) {
        if (otpCode === '000000') {
          return {
            success: false,
            message: 'Invalid verification code.',
            warning: 'The code you entered is incorrect. Please check and try again.'
          };
        }
        return {
          success: true,
          message: 'OTP verified successfully!'
        };
      }
      return {
        success: false,
        message: 'Connection failed.',
        warning: `Unable to contact OTP verification service at ${API_BASE_URL}.`
      };
    }

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || 'Verification failed.',
        warning: result?.warning || 'Incorrect verification code. Please check and try again.'
      };
    }

    // If backend returned a token after verification, save it.
    if (getAuthToken(result)) {
      const authEmail = getAuthUser(result)?.email || email;
      saveAuthSession(result, authEmail);
    }

    return {
      success: true,
      message: result?.message || 'OTP verified successfully!',
      data: result?.data || undefined,
    };
  } catch (error) {
    if (!API_CONFIG.isRealData) {
      return {
        success: true,
        message: 'OTP verified successfully!'
      };
    }
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to contact OTP verification service at ${API_BASE_URL}.`
    };
  }
}

/**
 * Resending OTP (POST)
 */
export async function apiResendOtp(email: string): Promise<ApiResponse> {
  if (!API_CONFIG.isRealData && !import.meta.env.VITE_API_BASE_URL) {
    await new Promise((r) => setTimeout(r, 600));
    return {
      success: true,
      message: 'A new verification code has been sent.'
    };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/resend-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(API_KEY ? { 'X-API-Key': API_KEY } : {})
      },
      body: JSON.stringify({ email })
    }).catch(() => null);

    if (!response) {
      if (!API_CONFIG.isRealData) {
        return {
          success: true,
          message: 'A new verification code has been sent.'
        };
      }
      return {
        success: false,
        message: 'Connection failed.',
        warning: `Unable to contact resend OTP service at ${API_BASE_URL}.`
      };
    }

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || 'Resend failed.',
        warning: result?.warning || 'Unable to resend OTP at this time.'
      };
    }

    return {
      success: true,
      message: result?.message || 'A new verification code has been sent.'
    };
  } catch (error) {
    if (!API_CONFIG.isRealData) {
      return {
        success: true,
        message: 'A new verification code has been sent.'
      };
    }
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to contact resend OTP service at ${API_BASE_URL}.`
    };
  }
}

/**
 * Starts the forgot-password flow.
 *
 * Tries /api/auth/forgot-password, then falls back to /api/auth/resend-otp.
 */
export async function apiRequestPasswordReset(email: string): Promise<ApiResponse> {
  if (!API_CONFIG.isRealData && !import.meta.env.VITE_API_BASE_URL) {
    await new Promise((r) => setTimeout(r, 600));
    return {
      success: true,
      message: 'Password reset code sent to your email.',
      data: { email }
    };
  }

  try {
    let response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(API_KEY ? { 'X-API-Key': API_KEY } : {})
      },
      body: JSON.stringify({ email })
    }).catch(() => null);

    if (!response || response.status === 404) {
      response = await fetch(`${API_BASE_URL}/api/auth/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(API_KEY ? { 'X-API-Key': API_KEY } : {})
        },
        body: JSON.stringify({ email })
      }).catch(() => null);
    }

    if (!response) {
      if (!API_CONFIG.isRealData) {
        return {
          success: true,
          message: 'Password reset code sent to your email.',
          data: { email }
        };
      }
      return {
        success: false,
        message: 'Connection failed.',
        warning: `Unable to reach backend API at ${API_BASE_URL}. Ensure your server is running and CORS is configured.`
      };
    }

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || 'Unable to send reset code.',
        warning: result?.warning || 'Please verify your registered email address.'
      };
    }

    return {
      success: true,
      message: result?.message || 'Password reset code sent to your email.',
      data: result?.data
    };
  } catch (error) {
    if (!API_CONFIG.isRealData) {
      return {
        success: true,
        message: 'Password reset code sent to your email.',
        data: { email }
      };
    }
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to contact password reset service at ${API_BASE_URL}.`
    };
  }
}

/**
 * Create/Update Password API request (POST)
 */
export async function apiUpdatePassword(email: string, password: string): Promise<ApiResponse> {
  if (!API_CONFIG.isRealData && !import.meta.env.VITE_API_BASE_URL) {
    await new Promise((r) => setTimeout(r, 600));
    localStorage.setItem('user_email', email);
    return {
      success: true,
      message: 'Your password has been updated successfully.'
    };
  }

  try {
    let response = await fetch(`${API_BASE_URL}/api/auth/update-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(API_KEY ? { 'X-API-Key': API_KEY } : {})
      },
      body: JSON.stringify({ email, password, newPassword: password, confirmPassword: password })
    }).catch(() => null);

    if (!response || response.status === 404) {
      response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(API_KEY ? { 'X-API-Key': API_KEY } : {})
        },
        body: JSON.stringify({ email, password, newPassword: password, confirmPassword: password })
      }).catch(() => null);
    }

    if (!response) {
      if (!API_CONFIG.isRealData) {
        localStorage.setItem('user_email', email);
        return {
          success: true,
          message: 'Your password has been updated successfully.'
        };
      }
      return {
        success: false,
        message: 'Connection failed.',
        warning: `Unable to contact password update service at ${API_BASE_URL}.`
      };
    }

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || 'Password update failed.',
        warning: result?.warning || 'Password does not meet requirements.'
      };
    }

    // Save email session details
    localStorage.setItem('user_email', email);

    return {
      success: true,
      message: result?.message || 'Your password has been updated successfully.'
    };
  } catch (error) {
    if (!API_CONFIG.isRealData) {
      localStorage.setItem('user_email', email);
      return {
        success: true,
        message: 'Your password has been updated successfully.'
      };
    }
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to contact password update service at ${API_BASE_URL}.`
    };
  }
}

/**
 * Log Out API request (POST)
 * Notifies backend to revoke session/token and cleans up localStorage.
 */
export async function apiLogout(): Promise<ApiResponse> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
  const refreshToken = typeof window !== 'undefined' ? localStorage.getItem('refresh_token') : null

  try {
    if (API_CONFIG.isRealData && token && token !== 'temp_demo_session') {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(API_KEY ? { 'X-API-Key': API_KEY } : {}),
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        headers,
        ...(refreshToken ? { body: JSON.stringify({ refresh: refreshToken }) } : {}),
      }).catch(() => null)

      if (response) {
        await response.json().catch(() => null)
      }
    }
  } catch {}

  // Always ensure client-side tokens are wiped
  try {
    clearAuthSession()
  } catch {}

  return {
    success: true,
    message: 'Logged out successfully',
  }
}